/** Prefer same-origin /api proxy on local CRA dev server (avoids CORS / hung cross-origin fetches). */
function resolveApiUrl(): string {
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    const { hostname, port } = window.location;
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '5000') {
      return '/api';
    }
  }
  const fromEnv = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  return 'http://localhost:5001/api';
}

const API_URL = resolveApiUrl();

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface ContactReply {
  _id?: string;
  author: 'customer' | 'admin';
  body: string;
  sentAt: string;
  emailSent?: boolean;
  adminName?: string;
}

export interface ContactPageData {
  page: {
    title: string;
    subtitle: string;
    bannerImageUrl?: string;
    bannerOverlayOpacity?: number;
    supportHeading?: string;
    supportText?: string;
  };
  contactInfo: {
    phones?: { label: string; value: string; enabled: boolean }[];
    emails?: { label: string; value: string; enabled: boolean }[];
    supportEmail?: string;
    officeAddress?: string;
    addressLine2?: string;
    businessHours?: { day: string; hours: string; enabled: boolean }[];
    onlineSupportNote?: string;
  };
  socialLinks: Record<string, { url: string; enabled: boolean }>;
  contactCards?: {
    icon: string;
    title: string;
    subtitle: string;
    value: string;
    enabled: boolean;
    order: number;
  }[];
  map: {
    enabled: boolean;
    heading?: string;
    embedUrl?: string;
    latitude?: number | null;
    longitude?: number | null;
    locationLabel?: string;
    openInMapsUrl?: string;
  };
  form: {
    enabled: boolean;
    heading?: string;
    description?: string;
    successTitle?: string;
    successMessage?: string;
    submitButtonText?: string;
    fields: Record<string, unknown>;
    autoReplyEnabled?: boolean;
    autoReplySubject?: string;
    autoReplyBody?: string;
    notifyAdminEmail?: boolean;
  };
  faq: {
    enabled: boolean;
    heading?: string;
    items?: { question: string; answer: string; enabled: boolean }[];
  };
  customSections?: { id: string; title: string; content: string; enabled: boolean; order: number }[];
  isPublished?: boolean;
}

export interface ContactInquiry {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'archived';
  read: boolean;
  adminReply?: string;
  repliedAt?: string;
  archivedAt?: string;
  createdAt: string;
  updatedAt?: string;
  replies?: ContactReply[];
}

async function fetchJsonWithTimeout(url: string, init?: RequestInit, ms = 20000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchPublicContactPage(): Promise<ContactPageData | null> {
  try {
    const res = await fetchJsonWithTimeout(`${API_URL}/contact/page`);
    const json = await res.json();
    if (!res.ok || !json.success) return null;
    return json.data;
  } catch {
    return null;
  }
}

export async function submitContactInquiry(payload: Record<string, string>) {
  let res: Response;
  try {
    res = await fetchJsonWithTimeout(`${API_URL}/contact/inquiries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Request timed out. Check that the backend is running on port 5001.');
    }
    throw new Error('Cannot reach server. Make sure the backend is running on port 5001.');
  }
  let json: { success?: boolean; message?: string; data?: { successTitle?: string; successMessage?: string } };
  try {
    json = await res.json();
  } catch {
    throw new Error('Invalid response from server. Please try again.');
  }
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Failed to send message');
  }
  return json.data as { successTitle?: string; successMessage?: string };
}

export async function fetchAdminContactPage(): Promise<ContactPageData> {
  const res = await fetch(`${API_URL}/admin/contact-page`, { headers: authHeaders() });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to load settings');
  return json.data;
}

export async function saveAdminContactPage(data: Partial<ContactPageData>) {
  const res = await fetch(`${API_URL}/admin/contact-page`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to save');
  return json.data;
}

export type InquiryListParams = {
  page?: number;
  limit?: number;
  status?: string;
  unread?: boolean;
  replied?: boolean;
  unreplied?: boolean;
  q?: string;
};

export async function fetchContactInquiries(params?: InquiryListParams) {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.status && params.status !== 'all') q.set('status', params.status);
  if (params?.unread) q.set('unread', 'true');
  if (params?.replied) q.set('replied', 'true');
  if (params?.unreplied) q.set('unreplied', 'true');
  if (params?.q) q.set('q', params.q);
  const res = await fetch(`${API_URL}/admin/contact-inquiries?${q}`, { headers: authHeaders() });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to load inquiries');
  return json as {
    success: boolean;
    data: ContactInquiry[];
    pagination: { page: number; limit: number; total: number; pages: number };
    unreadCount: number;
  };
}

export async function fetchContactInquiryStats() {
  const res = await fetch(`${API_URL}/admin/contact-inquiries/stats`, { headers: authHeaders() });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to load stats');
  return json.data as { total: number; unread: number; new: number; replied: number; archived: number };
}

export async function getContactInquiry(id: string) {
  const res = await fetch(`${API_URL}/admin/contact-inquiries/${id}`, { headers: authHeaders() });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to load inquiry');
  return json.data as ContactInquiry;
}

export async function markContactInquiryRead(id: string) {
  const res = await fetch(`${API_URL}/admin/contact-inquiries/${id}/read`, {
    method: 'PATCH',
    headers: authHeaders(),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to update');
  return json.data as ContactInquiry;
}

export async function markContactInquiryUnread(id: string) {
  const res = await fetch(`${API_URL}/admin/contact-inquiries/${id}/unread`, {
    method: 'PATCH',
    headers: authHeaders(),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to update');
  return json.data as ContactInquiry;
}

export async function replyContactInquiry(id: string, replyMessage: string, sendEmail = true) {
  const res = await fetch(`${API_URL}/admin/contact-inquiries/${id}/reply`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ replyMessage, sendEmail }),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to reply');
  return json.data as ContactInquiry;
}

export async function archiveContactInquiry(id: string) {
  const res = await fetch(`${API_URL}/admin/contact-inquiries/${id}/archive`, {
    method: 'PATCH',
    headers: authHeaders(),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to archive');
  return json.data as ContactInquiry;
}

export async function deleteContactInquiry(id: string) {
  const res = await fetch(`${API_URL}/admin/contact-inquiries/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.message || 'Failed to delete');
}
